// Signage → QR phygital (/signage/phygital). Cierra el bucle pantalla → teléfono:
// un QR en la vitrina que el cliente escanea para comprar EN LÍNEA el producto
// exhibido (deep-link al storefront) o abrir la tienda/monedero. Reusa el módulo
// e-commerce (/tienda/:slug). El QR se dibuja en el reproductor (funciona offline).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { AlertTriangle, ExternalLink, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Campo, Switch, fieldCls } from '@/features/pos/shared';
import { sigRpc } from './shared';

interface Estado {
  activo: boolean; modo: 'producto' | 'tienda' | 'url'; url_base: string | null; cta: string;
  url_personalizada: string | null; mostrar_en_producto: boolean; utm: string;
  tienda_slug: string | null; tienda_activa: boolean; productos_publicados: number;
}

export function Phygital() {
  const qc = useQueryClient();
  const { data } = useQuery<Estado>({ queryKey: ['sig-phygital'], queryFn: () => sigRpc<Estado>('sig_phygital_get'), staleTime: 0 });
  const [f, setF] = useState<Estado | null>(null);
  useEffect(() => { if (data) setF(data); }, [data]);

  const guardar = useMutation({
    mutationFn: (v: Estado) => sigRpc('sig_phygital_set', {
      _data: {
        activo: v.activo, modo: v.modo, url_base: v.url_base, cta: v.cta,
        url_personalizada: v.url_personalizada, mostrar_en_producto: v.mostrar_en_producto, utm: v.utm,
      },
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sig-phygital'] }); toast.success('Configuración guardada'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!f) return <div className="p-6 text-muted-foreground">Cargando…</div>;

  const base = (f.url_base || window.location.origin).replace(/\/+$/, '');
  const utm = f.utm ? `?utm_source=${encodeURIComponent(f.utm)}&utm_medium=signage` : '';
  const tiendaUrl = f.tienda_slug ? `${base}/tienda/${f.tienda_slug}${utm}` : '';
  const escenaUrl = f.modo === 'url' ? (f.url_personalizada || '') : tiendaUrl;
  const sinTienda = !f.tienda_activa && f.modo !== 'url';

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><QrCode className="w-5 h-5" /> QR phygital</h1>
      <p className="text-sm text-muted-foreground">Convierte la vitrina en un canal de venta: el cliente escanea el QR con su celular y compra <strong>en línea</strong> el producto que ve en pantalla, o abre tu tienda/monedero. Reutiliza tu tienda del módulo <strong>E-commerce</strong>.</p>

      {sinTienda && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>No hay una tienda en línea <strong>activa</strong> para esta empresa. Actívala en <strong>E-commerce → Tienda</strong> (o elige el destino "URL propia" abajo). El QR de producto solo funciona con productos publicados en la tienda.</span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Switch checked={f.activo} onChange={(v) => setF({ ...f, activo: v })} label="Activar QR phygital en el reproductor" />
          <Switch checked={f.mostrar_en_producto} onChange={(v) => setF({ ...f, mostrar_en_producto: v })} label="Mostrar mini-QR “compra este producto” sobre cada producto exhibido" />

          <Campo label="Texto de invitación (CTA)"><input className={fieldCls} maxLength={120} value={f.cta} onChange={(e) => setF({ ...f, cta: e.target.value })} placeholder="Escanéalo para comprarlo en línea" /></Campo>

          <Campo label="Destino de la escena QR grande">
            <select className={fieldCls} value={f.modo === 'url' ? 'url' : 'tienda'} onChange={(e) => setF({ ...f, modo: e.target.value as Estado['modo'] })}>
              <option value="tienda">Página principal de la tienda</option>
              <option value="url">URL propia (monedero, promo, WhatsApp…)</option>
            </select>
          </Campo>
          {f.modo === 'url' && (
            <Campo label="URL propia"><input className={fieldCls} value={f.url_personalizada ?? ''} onChange={(e) => setF({ ...f, url_personalizada: e.target.value })} placeholder="https://…" /></Campo>
          )}

          <Campo label="Dominio público del storefront (opcional)">
            <input className={fieldCls} value={f.url_base ?? ''} onChange={(e) => setF({ ...f, url_base: e.target.value })} placeholder="https://tudominio.com  (vacío = dominio del reproductor)" />
          </Campo>
          <Campo label="Etiqueta de atribución (UTM source)"><input className={fieldCls} maxLength={40} value={f.utm} onChange={(e) => setF({ ...f, utm: e.target.value })} placeholder="signage" /></Campo>

          <div className="flex items-center gap-3 pt-1">
            <Button onClick={() => guardar.mutate(f)} isLoading={guardar.isPending}>Guardar</Button>
            {f.tienda_activa && <span className="text-xs text-muted-foreground">{f.productos_publicados} producto(s) publicados · tienda <code>/{f.tienda_slug}</code></span>}
          </div>
        </div>

        {/* Vista previa del QR de la escena grande */}
        <div className="border rounded-lg p-5 flex flex-col items-center justify-center gap-3 bg-muted/20">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Vista previa</div>
          {escenaUrl ? (
            <>
              <div className="bg-white p-3 rounded-lg shadow-sm"><QRCodeSVG value={escenaUrl} size={190} level="M" /></div>
              <a href={escenaUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary inline-flex items-center gap-1 max-w-full break-all">
                <ExternalLink className="w-3 h-3 shrink-0" /> {escenaUrl}
              </a>
              <p className="text-xs text-muted-foreground text-center">{f.cta}</p>
            </>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-8">Configura un destino (tienda activa o URL propia) para ver el QR.</div>
          )}
        </div>
      </div>

      <div className="text-xs text-muted-foreground border-t pt-3 space-y-1">
        <p><strong>Cómo se ve en pantalla:</strong> el mini-QR aparece en la esquina del producto exhibido (solo si ese producto está publicado en la tienda). La <strong>escena QR</strong> se agrega a una plantilla en <em>Plantillas</em> (tipo “QR phygital”) para mostrar un QR grande a pantalla completa.</p>
        <p>El QR lleva la etiqueta UTM para que los pedidos originados en pantalla se reconozcan en las analíticas de la tienda.</p>
      </div>
    </div>
  );
}
